import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";

import {
  OPCIONES_DE_OBLIGATORIEDAD,
  Segmentado,
} from "@/components/plantillas/etiquetas";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { NodoDeGrupo, Obligatoriedad } from "@/lib/estructuraDePlantilla";
import type { MaestroItemConUso } from "@/lib/plantillas";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ */
/* Nuevo grupo / nuevo subgrupo                                         */
/* ------------------------------------------------------------------ */

export type NuevoBloque = { tipo: "grupo" } | { tipo: "subgrupo"; grupo: string };

interface NuevoBloqueDialogProps {
  bloque: NuevoBloque | null;
  existeGrupo: (nombre: string) => boolean;
  existeSubgrupo: (grupo: string, nombre: string) => boolean;
  onCrear: (bloque: NuevoBloque, nombre: string) => void;
  onCerrar: () => void;
}

/**
 * Un grupo es un capítulo del informe y un subgrupo ordena ítems dentro de él.
 * Nacen vacíos y SOLO en esta plantilla: no se crean en el maestro hasta que
 * un ítem que cuelga de ellos pasa al maestro.
 */
export function NuevoBloqueDialog(props: NuevoBloqueDialogProps) {
  return props.bloque ? <ContenidoDelBloque {...props} bloque={props.bloque} /> : null;
}

function ContenidoDelBloque({
  bloque,
  existeGrupo,
  existeSubgrupo,
  onCrear,
  onCerrar,
}: NuevoBloqueDialogProps & { bloque: NuevoBloque }) {
  const [nombre, setNombre] = useState("");
  const [error, setError] = useState<string | null>(null);
  const esGrupo = bloque.tipo === "grupo";

  const enviar = () => {
    const limpio = nombre.trim();
    if (!limpio) {
      setError(esGrupo ? "Ingresa el nombre del grupo." : "Ingresa el nombre del subgrupo.");
      return;
    }
    if (bloque.tipo === "grupo" && existeGrupo(limpio)) {
      setError("Ese grupo ya existe en la plantilla.");
      return;
    }
    if (bloque.tipo === "subgrupo" && existeSubgrupo(bloque.grupo, limpio)) {
      setError("Ese subgrupo ya existe en el grupo.");
      return;
    }
    onCrear(bloque, limpio);
  };

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {esGrupo ? "Nuevo grupo" : `Nuevo subgrupo en ${(bloque as { grupo: string }).grupo}`}
          </DialogTitle>
          <DialogDescription>
            {esGrupo
              ? "Un grupo es un capítulo del informe. Luego le agregas subgrupos o ítems."
              : "Un subgrupo ordena ítems dentro del grupo."}{" "}
            Se crea solo en esta plantilla.
          </DialogDescription>
        </DialogHeader>
        <form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            enviar();
          }}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="bloque-nombre">
              {esGrupo ? "Nombre del grupo" : "Nombre del subgrupo"}
            </Label>
            <Input
              id="bloque-nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              aria-invalid={Boolean(error)}
              autoFocus
            />
            {error ? <p className="text-destructive text-xs">{error}</p> : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCerrar}>
              Cancelar
            </Button>
            <Button type="submit">{esGrupo ? "Crear grupo" : "Crear subgrupo"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Mover                                                                */
/* ------------------------------------------------------------------ */

export type QueMover =
  | { tipo: "item"; ref: string; nombre: string; grupo: string; subgrupo: string | null }
  | { tipo: "subgrupo"; grupo: string; subgrupo: string | null };

interface MoverDialogProps {
  que: QueMover | null;
  arbol: NodoDeGrupo[];
  onMoverItem: (ref: string, grupo: string, subgrupo: string | null) => void;
  onMoverSubgrupo: (grupo: string, subgrupo: string | null, destino: string) => void;
  onCerrar: () => void;
}

/** "Mover a otro grupo": elegir un destino de la lista, agrupada por grupo. */
export function MoverDialog({ que, arbol, onMoverItem, onMoverSubgrupo, onCerrar }: MoverDialogProps) {
  if (!que) return null;

  const esItem = que.tipo === "item";
  const titulo = esItem ? `Mover “${que.nombre}”` : `Mover subgrupo “${que.subgrupo}”`;

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>
            {esItem
              ? "Elige el grupo y subgrupo de destino."
              : "Elige el grupo de destino. Sus ítems se mueven con él."}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[50vh] overflow-y-auto rounded-md border">
          {arbol.map((nodo) => {
            const opciones: { subgrupo: string | null; etiqueta: string }[] = esItem
              ? [
                  { subgrupo: null, etiqueta: "Sin subgrupo" },
                  ...nodo.subgrupos
                    .filter((s) => s.subgrupo !== null)
                    .map((s) => ({ subgrupo: s.subgrupo, etiqueta: s.subgrupo as string })),
                ]
              : [];

            return (
              <div key={nodo.grupo} className="border-divisor-suave border-b last:border-b-0">
                {esItem ? (
                  <>
                    <p className="text-ink bg-superficie-sutil px-3 py-1.5 text-xs font-extrabold">
                      {nodo.grupo}
                    </p>
                    {opciones.map((o) => {
                      const actual = que.grupo === nodo.grupo && que.subgrupo === o.subgrupo;
                      return (
                        <button
                          key={o.subgrupo ?? "—"}
                          type="button"
                          disabled={actual}
                          onClick={() => onMoverItem(que.ref, nodo.grupo, o.subgrupo)}
                          className={cn(
                            "hover:bg-superficie-sutil flex w-full items-center justify-between py-2 pr-3 pl-6 text-left text-sm outline-none focus-visible:bg-superficie-sutil",
                            o.subgrupo === null ? "text-ink-muted" : "text-ink",
                            actual && "bg-muted/60 cursor-default",
                          )}
                        >
                          {o.etiqueta}
                          {actual ? (
                            <span className="text-ink-hint text-xs">Ubicación actual</span>
                          ) : null}
                        </button>
                      );
                    })}
                  </>
                ) : (
                  <button
                    type="button"
                    disabled={que.grupo === nodo.grupo}
                    onClick={() => onMoverSubgrupo(que.grupo, que.subgrupo, nodo.grupo)}
                    className={cn(
                      "hover:bg-superficie-sutil text-ink flex w-full items-center justify-between px-3 py-2 text-left text-sm outline-none focus-visible:bg-superficie-sutil",
                      que.grupo === nodo.grupo && "bg-muted/60 cursor-default",
                    )}
                  >
                    {nodo.grupo}
                    {que.grupo === nodo.grupo ? (
                      <span className="text-ink-hint text-xs">Ubicación actual</span>
                    ) : null}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCerrar}>
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Quitar un ítem                                                       */
/* ------------------------------------------------------------------ */

interface QuitarItemDialogProps {
  item: { nombre: string; local: boolean } | null;
  /** En cuántas OTRAS plantillas está (solo para ítems compartidos). */
  enOtrasPlantillas: number;
  onConfirmar: () => void;
  onCancelar: () => void;
}

export function QuitarItemDialog({
  item,
  enOtrasPlantillas,
  onConfirmar,
  onCancelar,
}: QuitarItemDialogProps) {
  return (
    <AlertDialog open={item !== null} onOpenChange={(abierto) => !abierto && onCancelar()}>
      <AlertDialogContent>
        {item ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {item.local
                  ? `¿Eliminar “${item.nombre}”?`
                  : `¿Quitar “${item.nombre}” de la plantilla?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {item.local
                  ? "Este ítem solo existe en esta plantilla. Se eliminará por completo al guardar y no se puede recuperar."
                  : `El ítem seguirá en el maestro${
                      enOtrasPlantillas > 0
                        ? ` y en ${enOtrasPlantillas} plantilla${enOtrasPlantillas > 1 ? "s" : ""} más`
                        : ""
                    }. Puedes volver a agregarlo con “Agregar del maestro”.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={onConfirmar}
                className="bg-destructive hover:bg-destructive/90 text-white"
              >
                {item.local ? "Eliminar ítem" : "Quitar de la plantilla"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : null}
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ------------------------------------------------------------------ */
/* Agregar del maestro                                                  */
/* ------------------------------------------------------------------ */

interface AgregarDelMaestroDialogProps {
  abierto: boolean;
  /** Ítems compartidos que todavía no están en la plantilla. */
  disponibles: MaestroItemConUso[];
  onAgregar: (items: MaestroItemConUso[], obligatoriedad: Obligatoriedad) => void;
  onCerrar: () => void;
}

export function AgregarDelMaestroDialog(props: AgregarDelMaestroDialogProps) {
  return props.abierto ? <ContenidoDelMaestro {...props} /> : null;
}

function ContenidoDelMaestro({ disponibles, onAgregar, onCerrar }: AgregarDelMaestroDialogProps) {
  const [busqueda, setBusqueda] = useState("");
  const [elegidos, setElegidos] = useState<Set<number>>(new Set());
  const [como, setComo] = useState<Obligatoriedad>("opcional");

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return disponibles;
    return disponibles.filter((i) =>
      `${i.nombre} ${i.descripcion ?? ""} ${i.subgrupo ?? ""} ${i.grupo}`.toLowerCase().includes(q),
    );
  }, [disponibles, busqueda]);

  const secciones = useMemo(() => {
    const lista: { titulo: string; items: MaestroItemConUso[] }[] = [];
    for (const item of visibles) {
      const titulo = item.subgrupo ? `${item.grupo} · ${item.subgrupo}` : item.grupo;
      const ultima = lista[lista.length - 1];
      if (ultima?.titulo === titulo) ultima.items.push(item);
      else lista.push({ titulo, items: [item] });
    }
    return lista;
  }, [visibles]);

  const alternar = (id: number) =>
    setElegidos((actual) => {
      const siguiente = new Set(actual);
      if (siguiente.has(id)) siguiente.delete(id);
      else siguiente.add(id);
      return siguiente;
    });

  const cantidad = elegidos.size;

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[85vh] flex-col gap-0 overflow-hidden rounded-xl p-0 sm:max-w-[45rem]"
      >
        <div className="border-divisor flex flex-none flex-col gap-3 border-b px-5.5 pt-5 pb-3.5">
          <div className="flex items-center gap-2.5">
            <div className="flex-1">
              <DialogTitle className="text-h3">Agregar ítems del maestro</DialogTitle>
              <DialogDescription className="text-ink-hint mt-0.5 text-[0.78rem] font-semibold">
                {disponibles.length} ítems del maestro aún no están en esta plantilla.
              </DialogDescription>
            </div>
            <DialogClose
              aria-label="Cerrar"
              title="Cerrar"
              className="bg-muted/60 text-ink-body hover:bg-muted focus-visible:ring-ring/50 flex size-[2.125rem] shrink-0 items-center justify-center rounded-[0.625rem] outline-none focus-visible:ring-[3px]"
            >
              <X className="size-4" aria-hidden="true" />
            </DialogClose>
          </div>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="text-ink-hint pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2"
            />
            <Input
              aria-label="Buscar en el maestro"
              placeholder="Buscar ítems disponibles…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <div className="min-h-[12.5rem] flex-1 overflow-y-auto px-3 pt-1.5 pb-3">
          {secciones.length === 0 ? (
            <p className="text-ink-hint px-5 py-10 text-center text-[0.8125rem] font-semibold">
              {disponibles.length === 0
                ? "Todos los ítems del maestro ya están en esta plantilla."
                : "No hay ítems disponibles con ese criterio."}
            </p>
          ) : (
            secciones.map((seccion) => (
              <div key={seccion.titulo}>
                <p className="text-ink-muted px-2 pt-3 pb-1 text-[0.6875rem] font-extrabold tracking-[0.04em] uppercase">
                  {seccion.titulo}
                </p>
                {seccion.items.map((item) => (
                  <label
                    key={item.id}
                    className={cn(
                      "hover:bg-superficie-sutil flex cursor-pointer items-center gap-2.5 rounded-[0.625rem] p-2",
                      elegidos.has(item.id) && "bg-success-soft/50",
                    )}
                  >
                    <Checkbox
                      checked={elegidos.has(item.id)}
                      onCheckedChange={() => alternar(item.id)}
                      aria-label={item.nombre}
                    />
                    <span className="flex min-w-0 flex-1 flex-col gap-px">
                      <span className="text-ink text-[0.8125rem] font-semibold">{item.nombre}</span>
                      {item.descripcion ? (
                        <span className="text-ink-hint truncate text-[0.71875rem] font-medium">
                          {item.descripcion}
                        </span>
                      ) : null}
                    </span>
                  </label>
                ))}
              </div>
            ))
          )}
        </div>

        <div className="border-divisor flex flex-none flex-wrap items-center gap-3 border-t px-5.5 py-3.5">
          <span className="text-ink-body text-xs font-bold">Agregar como</span>
          <Segmentado<Obligatoriedad>
            etiqueta="Agregar como"
            valor={como}
            onChange={setComo}
            opciones={OPCIONES_DE_OBLIGATORIEDAD}
          />
          <Button
            type="button"
            disabled={cantidad === 0}
            onClick={() => onAgregar(disponibles.filter((i) => elegidos.has(i.id)), como)}
            className="ml-auto disabled:bg-ink-ghost disabled:text-white disabled:opacity-100 disabled:shadow-none"
          >
            {cantidad === 0
              ? "Selecciona ítems"
              : `Agregar ${cantidad} ítem${cantidad > 1 ? "s" : ""}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
